import {
  DocumentPackage,
  OFFER_PROCESSOR_CATEGORIES,
  ProcessorPreferenceKey,
} from '@consigli/types';

/** Maximum number of bids (manual + other) allowed in a single comparison. */
export const MAX_COMPARISON_BIDS = 5;

export interface Bid {
  id: string;
  providerName: string;
  language: string;
  files: File[];
}

export const createEmptyBid = (): Bid => ({
  id: crypto.randomUUID(),
  providerName: '',
  language: '',
  files: [],
});

export enum BidReviewService {
  RISK_INSIGHT_GAPS = 'RISK_INSIGHT_GAPS',
  BID_VS_BID = 'BID_VS_BID',
}

export enum GapsCompareMode {
  EXISTING = 'EXISTING',
  UPLOAD_NEW = 'UPLOAD_NEW',
}

// A bid from another Offer package on the project, offered as an extra
// comparison target in the bid vs. bid step. Identified by its package and
// provider name because a package can hold several bids.
export interface OtherBidReference {
  packageId: string;
  packageName: string;
  providerName: string;
}

export interface CompareConfig {
  services: BidReviewService[];
  gapsCompareMode: GapsCompareMode;
  existingReferencePackageId: string;
  newReferenceFiles: File[];
  selectedProcessors: ProcessorPreferenceKey[];
  autoFillLevelingSheet: boolean;
  levelingSheetFiles: File[];
  selectedOtherBids: OtherBidReference[];
}

const allOfferProcessors = (): ProcessorPreferenceKey[] => [
  ...OFFER_PROCESSOR_CATEGORIES.RISK.map((option) => option.key),
  ...OFFER_PROCESSOR_CATEGORIES.INSIGHT.map((option) => option.key),
  ...OFFER_PROCESSOR_CATEGORIES.GAPS.map((option) => option.key),
];

export const createDefaultCompareConfig = (): CompareConfig => ({
  services: [],
  gapsCompareMode: GapsCompareMode.EXISTING,
  existingReferencePackageId: '',
  newReferenceFiles: [],
  selectedProcessors: allOfferProcessors(),
  autoFillLevelingSheet: false,
  levelingSheetFiles: [],
  selectedOtherBids: [],
});

// Key under DocumentPackage.data holding the bid-review services the user
// picked in the wizard. Persisted at creation and read back to drive pipeline
// branching (breeze) and tab visibility for the created Offer package. Kept a
// single word so it survives the snake/camel case round-trip unchanged.
export const BID_REVIEW_SERVICES_DATA_KEY = 'services';

export const getSelectedBidReviewServices = (
  documentPackage: DocumentPackage,
): BidReviewService[] => {
  const stored = documentPackage.data?.[BID_REVIEW_SERVICES_DATA_KEY];
  if (!Array.isArray(stored)) return [];
  const known = new Set<string>(Object.values(BidReviewService));
  return stored.filter((value): value is BidReviewService => known.has(value as string));
};
